/**
 * cart-attrs-sync.js — Keep cart.attributes in sync with actual line items.
 *
 * Background:
 *   The customizers (starmap-v2.js, locationmap.js, moon-myjs.js, etc.) write
 *   gift state to cart.attributes ON ADD-TO-CART:
 *     _custom_bundle_active, _custom_bundle_quantity,
 *     _addon_ribbon_color, _addon_gift_wrap_theme, _addon_gift_note
 *   AND push the corresponding gift-product line item (Ribbon / Gift Wrap /
 *   Bundle variant) to /cart/add.js.
 *
 *   If the customer later removes the gift line item from the cart drawer or
 *   cart page, the line is gone but the attributes persist. The order then
 *   ships with note_attributes claiming a bundle was selected, but no charge
 *   for it and no SKU for fulfilment to pick. (Real example: Shopify order
 *   #166205 / SkylitStudio#166205 / 2026-05-06.)
 *
 * What this does:
 *   - On page load, fetch /cart.js and reconcile attributes against line
 *     items. If gift attrs claim "bundle" / "ribbon" / "giftwrap" but the
 *     matching variant isn't in the cart, clear the stale attrs.
 *   - After every /cart/{add,change,clear} mutation, run the same check.
 *
 * Variant IDs: pulled from starmap-v2.js / locationmap.js GIFT_IDS. If they
 * change, update them here too (and DRY them out into a single window global
 * eventually).
 */
(function() {
  if (window.__cartAttrsSyncLoaded) return;
  window.__cartAttrsSyncLoaded = true;

  /* @lm-test-export:start buildPatch */
  // Gift wrap and the bundle are size-tiered (multiple variants per
  // product), so gift lines are recognised by PRODUCT handle, never by a
  // hardcoded variant id.
  var GIFT_HANDLES = {
    'gift-wrapping-ribbon': 'ribbon',
    'gift-wrap': 'giftwrap',
    'gift-bundle': 'bundle'
  };
  function giftKind(item) {
    return GIFT_HANDLES[item && item.handle] || null;
  }

  function detectGiftFromItems(items) {
    if (!items || !items.length) return 'none';
    var kinds = items.map(giftKind);
    var hasBundle   = kinds.indexOf('bundle')   !== -1;
    var hasGiftwrap = kinds.indexOf('giftwrap') !== -1;
    var hasRibbon   = kinds.indexOf('ribbon')   !== -1;
    if (hasBundle) return 'bundle';
    if (hasGiftwrap && hasRibbon) return 'bundle';
    if (hasGiftwrap) return 'giftwrap';
    if (hasRibbon)   return 'ribbon';
    return 'none';
  }

  // Build a patch object containing only the attrs that need changing.
  // Empty string = clear the attribute on Shopify's side.
  function buildPatch(actualGift, attrs, items) {
    attrs = attrs || {};
    var patch = {};
    var has = function(k) { return attrs[k] != null && attrs[k] !== ''; };
    var bundleQuantity = (items || []).reduce(function(total, item) {
      return total + (giftKind(item) === 'bundle' ? Number(item.quantity || 0) : 0);
    }, 0);

    // Mirror the paper design chosen on the paired wrap/bundle line into the
    // cart-wide attribute so fulfilment data reflects the customer's actual
    // choice, not whatever design was selected first. The NEWEST paired line
    // with a design wins, and /cart.js lists the newest line FIRST (there is
    // only ever one wrap/bundle line per print by the time
    // excessPairedAddonMutations has run).
    var pairedTheme = '';
    (items || []).forEach(function(item) {
      var k = giftKind(item);
      if (!pairedTheme && (k === 'giftwrap' || k === 'bundle') && item.properties && item.properties._gift_wrap_theme) pairedTheme = item.properties._gift_wrap_theme;
    });

    if (actualGift === 'none') {
      if (attrs._custom_bundle_active === 'true' || has('_custom_bundle_active')) patch._custom_bundle_active = '';
      if (has('_custom_bundle_quantity')) patch._custom_bundle_quantity = '';
      if (has('_addon_ribbon_color'))     patch._addon_ribbon_color = '';
      if (has('_addon_gift_wrap_theme'))  patch._addon_gift_wrap_theme = '';
      if (has('_addon_gift_note'))        patch._addon_gift_note = '';
    } else if (actualGift === 'ribbon') {
      if (attrs._custom_bundle_active === 'true') patch._custom_bundle_active = 'false';
      if (has('_addon_gift_wrap_theme')) patch._addon_gift_wrap_theme = '';
    } else if (actualGift === 'giftwrap') {
      if (attrs._custom_bundle_active === 'true') patch._custom_bundle_active = 'false';
      if (has('_addon_ribbon_color')) patch._addon_ribbon_color = '';
      if (pairedTheme && attrs._addon_gift_wrap_theme !== pairedTheme) patch._addon_gift_wrap_theme = pairedTheme;
    } else if (actualGift === 'bundle') {
      if (attrs._custom_bundle_active !== 'true') patch._custom_bundle_active = 'true';
      var expectedBundleQuantity = String(bundleQuantity || 1);
      if (String(attrs._custom_bundle_quantity || '') !== expectedBundleQuantity) {
        patch._custom_bundle_quantity = expectedBundleQuantity;
      }
      if (pairedTheme && attrs._addon_gift_wrap_theme !== pairedTheme) patch._addon_gift_wrap_theme = pairedTheme;
    }
    return patch;
  }
  /* @lm-test-export:end buildPatch */

  var ADMIN_BASE = 'https://admin.skylitstudio.co.uk/';

  // Pull every customizer order ID present in the cart. Customizer line items
  // ship an _OrderID property; gift-only line items don't. We sync gift state
  // for each owning order.
  function customizerOrderIds(items) {
    if (!items) return [];
    var ids = [];
    items.forEach(function(item) {
      var props = item.properties || {};
      var oid = props._OrderID || props.OrderID;
      if (oid && ids.indexOf(oid) === -1) ids.push(oid);
    });
    return ids;
  }

  // Per-print gift resolution. The old cart-wide model stamped ONE gift
  // state onto EVERY print's admin row — two prints with a ribbon paired to
  // print A only also recorded a ribbon on print B's fulfilment data (the
  // inverse of the #166205 bug). Gift lines carry _paired_with_order_id, so
  // attribute each gift to its own print; legacy unpaired gift lines fall
  // back to the old cart-wide value for prints without a paired gift.
  function perOrderGifts(items) {
    var orders = customizerOrderIds(items);
    var flags = {};
    orders.forEach(function(o) { flags[o] = { ribbon: false, giftwrap: false, bundle: false }; });
    var unpaired = 0;
    (items || []).forEach(function(item) {
      var kind = giftKind(item);
      if (!kind) return;
      var paired = item.properties && item.properties._paired_with_order_id;
      if (paired && flags[paired]) flags[paired][kind] = true;
      else unpaired++;
    });
    var gifts = {};
    orders.forEach(function(o) {
      var f = flags[o];
      gifts[o] = f.bundle ? 'bundle'
        : (f.giftwrap && f.ribbon) ? 'bundle'
        : f.giftwrap ? 'giftwrap'
        : f.ribbon ? 'ribbon' : 'none';
    });
    if (unpaired) {
      var legacyGift = detectGiftFromItems(items);
      orders.forEach(function(o) { if (gifts[o] === 'none') gifts[o] = legacyGift; });
    }
    return gifts;
  }

  // Per-print frame add-on ("Complete the look"). A frame bought from the cart
  // upsell is its OWN line item paired to the print, so the admin order list —
  // which reads the design's variant — showed those orders as plain
  // "Unframed". Report the frame per design so the list can chip it.
  //
  // Pairing precedence mirrors the server's (app/lib/shopify-fulfilment.ts):
  // line ref first, then the legacy cart key, then _frame_for_order_id. A
  // design with no frame is reported EXPLICITLY as null — the admin treats an
  // absent key as "unknown" (old cached theme JS) and only clears a stored
  // frame on an explicit null.
  var FRAME_HANDLE = 'skylit-studio-frame';
  function perOrderFrames(items) {
    var frames = {};
    customizerOrderIds(items).forEach(function(o) { frames[o] = null; });

    var byRef = {}, byKey = {};
    (items || []).forEach(function(item) {
      var p = item.properties || {};
      var oid = p._OrderID || p.OrderID;
      if (!oid) return;
      byKey[item.key] = oid;
      // Before a quantity increase migrates a print to an explicit UUID, cart
      // controls use its current line key as the copy ref.
      byRef[p._cart_line_ref || item.key] = oid;
    });

    (items || []).forEach(function(item) {
      if (item.handle !== FRAME_HANDLE) return;
      var p = item.properties || {};
      var owner = (p._paired_with_line_ref && byRef[p._paired_with_line_ref])
        || (p._paired_with_key && byKey[p._paired_with_key])
        || (p._frame_for_order_id && frames.hasOwnProperty(p._frame_for_order_id)
              ? p._frame_for_order_id : null);
      // No owner = a frame bought on its own, not an upsell. Leave it alone.
      // Newest line first in /cart.js, so the first frame seen is the latest.
      if (!owner || frames[owner]) return;
      var opts = item.variant_options || [];
      frames[owner] = {
        colour: opts[0] || '',
        size: opts[1] || '',
        quantity: Number(item.quantity || 1) || 1
      };
    });
    return frames;
  }

  // Track which customizer order ids THIS browser has seen in its cart, so a
  // later sync can tell "was in cart, now gone" (removed by the customer)
  // from "never made it to cart". Reported ids get funnel_status
  // 'cart_removed' server-side (only if currently 'cart' — a checkout that
  // empties the cart is protected by the paid status landing first).
  var SEEN_KEY = 'skylit_cart_seen_order_ids';
  var SEEN_TTL_MS = 30 * 86400000;
  function readSeen() {
    try {
      var obj = JSON.parse(localStorage.getItem(SEEN_KEY) || '{}') || {};
      var now = Date.now(), out = {};
      Object.keys(obj).forEach(function(k) {
        if (now - (obj[k] || 0) < SEEN_TTL_MS) out[k] = obj[k];
      });
      return out;
    } catch (e) { return {}; }
  }
  function diffRemovedAndRecordSeen(liveIds) {
    try {
      var seen = readSeen();
      var removed = Object.keys(seen).filter(function(k) { return liveIds.indexOf(k) === -1; });
      var next = {};
      liveIds.forEach(function(id) { next[id] = seen[id] || Date.now(); });
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
      return removed;
    } catch (e) { return []; }
  }

  function syncAdmin(gifts, removedIds, frames) {
    removedIds = removedIds || [];
    frames = frames || null;
    var hasFrames = frames && Object.keys(frames).length;
    if (!Object.keys(gifts).length && !removedIds.length && !hasFrames) return Promise.resolve();
    var body = { gifts: gifts };
    if (removedIds.length) body.removed_order_ids = removedIds;
    if (hasFrames) body.frames = frames;
    return fetch(ADMIN_BASE + 'api/cart-sync', {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function(r) { return r.json(); })
      .then(function(j) {
        if (j && j.updated && j.updated.length) {
          console.log('[cart-attrs-sync] admin updated', j.updated);
        }
        return j;
      })
      .catch(function(e) { console.warn('[cart-attrs-sync] admin sync failed', e); });
  }

  // Identify add-on lines that are explicitly paired to a print which is no
  // longer in the cart. This is intentionally property-driven instead of
  // variant-driven: ribbon, gift wrap, bundles and adhesive wall strips all
  // use the same pairing contract, and future paired add-ons should inherit
  // the same orphan protection automatically.
  function orphanPairedAddonKeys(items) {
    if (!items || !items.length) return [];
    var liveOrderIds = {};
    var liveLineRefs = {};
    items.forEach(function(item) {
      var props = item.properties || {};
      var oid = props._OrderID || props.OrderID;
      if (oid) {
        liveOrderIds[oid] = true;
        // Before a quantity increase migrates the print to an explicit UUID,
        // cart controls use its current Shopify line key as the copy ref.
        liveLineRefs[props._cart_line_ref || item.key] = true;
      }
    });
    var keys = [];
    items.forEach(function(item) {
      var props = item.properties || {};
      var pairedTo = props._paired_with_order_id;
      if (!pairedTo) return;
      if (props._paired_with_line_ref) {
        if (!liveLineRefs[props._paired_with_line_ref]) keys.push(item.key);
      } else if (!liveOrderIds[pairedTo]) {
        keys.push(item.key);
      }
    });
    return keys;
  }

  /* @lm-test-export:start excessPairedAddonMutations */
  // How many print units each pairing target currently has in the cart.
  function pairedPrintUnits(items) {
    var byLineRef = {}, byOrderId = {};
    (items || []).forEach(function(item) {
      var props = item.properties || {};
      var oid = props._OrderID || props.OrderID;
      if (!oid) return;
      var qty = Number(item.quantity || 0);
      var ref = props._cart_line_ref || item.key;
      byLineRef[ref] = (byLineRef[ref] || 0) + qty;
      byOrderId[oid] = (byOrderId[oid] || 0) + qty;
    });
    return { byLineRef: byLineRef, byOrderId: byOrderId };
  }

  /**
   * A print can only ever own ONE of each paid add-on, and never more units
   * than there are prints to put them on. Two things used to break that:
   *   - a second add-to-cart from a stale customizer page pushed a second
   *     charged gift line (SkylitStudio#191407: one moon map, two bundles);
   *   - decrementing a grouped print's quantity left its add-on units behind.
   * Both are invisible in the cart, because paired lines render inside the
   * print's row instead of as their own row.
   *
   * Returns [{ id, quantity }] mutations. Only ever REDUCES: an add-on the
   * customer chose is never silently upgraded to a bigger charge, and lines of
   * different PRODUCTS (a separate ribbon and gift wrap on one print) are left
   * alone, but two lines of the same wrap product, whatever their size tier or
   * paper design, collapse to one, since a print can only ever wear one wrap.
   *
   * `items` must be in /cart.js order: Shopify lists the NEWEST line FIRST
   * (a new line goes to the top; merging into an existing line or changing
   * its quantity does not move it). Verified on the live store 2026-09-26.
   */
  function excessPairedAddonMutations(items) {
    var units = pairedPrintUnits(items);
    var groups = {};
    (items || []).forEach(function(item) {
      var props = item.properties || {};
      var target = props._paired_with_line_ref || props._paired_with_order_id;
      if (!target) return;
      var byRef = !!props._paired_with_line_ref;
      var key = (byRef ? 'ref:' : 'oid:') + target + '|' + String(item.handle || item.product_id || item.variant_id);
      if (!groups[key]) {
        groups[key] = {
          lines: [],
          allowed: byRef
            ? (units.byLineRef[target] || 0)
            : (units.byOrderId[target] || 0)
        };
      }
      groups[key].lines.push(item);
    });

    var mutations = [];
    Object.keys(groups).forEach(function(key) {
      var group = groups[key];
      // No print left at all — orphanPairedAddonKeys owns that case.
      if (!group.allowed) return;
      var total = group.lines.reduce(function(sum, line) {
        return sum + Number(line.quantity || 0);
      }, 0);
      if (total <= group.allowed) return;

      // Keep the line carrying the customer's gift note (and its card design)
      // ahead of a bare duplicate, then keep the NEWEST line over older ones:
      // it is the customer's current choice (a paper or size-tier swap adds
      // the new variant after the old one). Newest is the LOWEST index, since
      // /cart.js lists the newest line first. This used to sort the other
      // way and kept the oldest line, which dropped the new tier mid-swap.
      var ordered = group.lines.slice().sort(function(a, b) {
        var an = (a.properties || {})._gift_note ? 0 : 1;
        var bn = (b.properties || {})._gift_note ? 0 : 1;
        if (an !== bn) return an - bn;
        return group.lines.indexOf(a) - group.lines.indexOf(b);
      });
      var budget = group.allowed;
      ordered.forEach(function(line) {
        var qty = Number(line.quantity || 0);
        var keep = Math.min(qty, budget);
        budget -= keep;
        if (keep !== qty) mutations.push({ id: line.key, quantity: keep });
      });
    });
    return mutations;
  }
  /* @lm-test-export:end excessPairedAddonMutations */

  function announceCartChange() {
    // Trigger the cart-frame-upsell.js cart:refresh listener so cart sections
    // repaint and the removed line disappears visually as well as server-side.
    // Otherwise the customer sees a stale gift line until they reload.
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-attrs-sync' } }));
    } catch (e) {}
  }

  /**
   * Apply ONE mutation per pass, re-reading the cart between passes. This store
   * re-hashes line keys after a mutation, so a chain of changes built from a
   * single cart snapshot silently no-ops on every key after the first.
   * `compute(items)` returns the next {id, quantity} mutation, or null when the
   * cart already satisfies the invariant.
   */
  var MAX_PASSES = 12;
  function reconcileLoop(label, compute) {
    var changed = false;
    function pass(n) {
      if (n >= MAX_PASSES) {
        console.warn('[cart-attrs-sync] ' + label + ' hit the pass ceiling');
        return Promise.resolve(changed);
      }
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          if (window.__skylitCartEditInFlight) return changed;
          var mutation = compute(cart.items);
          if (!mutation) return changed;
          console.log('[cart-attrs-sync] ' + label + ':', mutation);
          return fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(mutation)
          }).then(function(r) { return r.json().catch(function() { return null; }); })
            .then(function() {
              changed = true;
              return pass(n + 1);
            });
        });
    }
    return pass(0)
      .then(function(result) {
        if (changed) announceCartChange();
        return result;
      })
      .catch(function(e) {
        console.warn('[cart-attrs-sync] ' + label + ' failed', e);
        return changed;
      });
  }

  // A trigger that lands while a sync is running (a burst of adds, or the
  // sync's own trims) queues ONE re-run instead of being dropped. Dropping it
  // left the last state of a quick add/add/add unchecked: a duplicate wrap
  // stayed in the basket until the next cart change or page load.
  var syncing = false;
  var resyncQueued = false;
  var settleTimer = null;
  function deferUntilEditSettles() {
    if (settleTimer) return;
    settleTimer = setInterval(function() {
      if (window.__skylitCartEditInFlight) return;
      clearInterval(settleTimer);
      settleTimer = null;
      syncAttrs();
    }, 300);
  }
  function syncAttrs() {
    if (syncing) { resyncQueued = true; return Promise.resolve(); }
    syncing = true;
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // First pass: reap orphan paired add-ons. If any are removed, refetch
        // the cart so subsequent reconciliation operates on fresh state.
        // Skip during a customizer edit-replace (remove-old-print +
        // add-new-print is non-atomic; the cart is momentarily orphaned
        // mid-flight and we'd wipe gifts that are about to be re-paired).
        if (window.__skylitCartEditInFlight) {
          console.log('[cart-attrs-sync] edit in flight, skipping orphan reap');
          return cart;
        }
        if (!orphanPairedAddonKeys(cart.items).length) return cart;
        return reconcileLoop('orphan reap', function(items) {
          var keys = orphanPairedAddonKeys(items);
          return keys.length ? { id: keys[0], quantity: 0 } : null;
        }).then(function() {
          return fetch('/cart.js', { credentials: 'same-origin' })
            .then(function(r) { return r.json(); });
        });
      })
      .then(function(cart) {
        // Second pass: one add-on per print, never more units than prints.
        // Runs after the orphan reap so both operate on fresh cart state.
        if (window.__skylitCartEditInFlight) return cart;
        if (!excessPairedAddonMutations(cart.items).length) return cart;
        return reconcileLoop('excess add-on trim', function(items) {
          return excessPairedAddonMutations(items)[0] || null;
        }).then(function() {
          return fetch('/cart.js', { credentials: 'same-origin' })
            .then(function(r) { return r.json(); });
        });
      })
      .then(function(cart) {
        // Mid edit-replace, write nothing at all. Shopify's /cart/update.js
        // saves the whole cart it read, so one landing while a customizer's
        // purge removes the old print puts that print back (reproduced 4/20
        // on the live store; a gallery wall star map edit ended with two
        // prints, 2026-09-29). It also reported the momentary "no gift" to
        // admin. Re-run once the edit has settled.
        if (window.__skylitCartEditInFlight) {
          deferUntilEditSettles();
          return;
        }
        var actualGift = detectGiftFromItems(cart.items);
        var patch = buildPatch(actualGift, cart.attributes, cart.items);

        // Mirror the gift state to skylit-admin so the fulfilment PDF
        // reflects what the customer actually has in cart, not what they
        // last selected in the customizer — resolved PER PRINT via
        // _paired_with_order_id (cart attributes above stay cart-wide).
        // Also report designs that left the cart (seen-set diff) so their
        // rows show "removed from cart" instead of reverting to a plain
        // draft. (Mid edit-replace never gets here, see above: the momentary
        // gap would false-flag the edited design as removed and frameless.)
        var removedIds = diffRemovedAndRecordSeen(customizerOrderIds(cart.items));
        var frames = perOrderFrames(cart.items);
        var adminPromise = syncAdmin(perOrderGifts(cart.items), removedIds, frames);

        if (!Object.keys(patch).length) return adminPromise;
        console.log('[cart-attrs-sync] reconciling: actualGift=' + actualGift + ' patch=', patch);
        var attrPromise = fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: patch })
        }).then(function(r) { return r.json(); });
        return Promise.all([attrPromise, adminPromise]).then(function(v) { return v[0]; });
      })
      .catch(function(e) { console.warn('[cart-attrs-sync]', e); })
      .then(function(v) { syncing = false; return v; }, function(e) { syncing = false; throw e; })
      .then(function(v) {
        if (resyncQueued) { resyncQueued = false; setTimeout(syncAttrs, 50); }
        return v;
      });
  }

  // Re-sync after any line-item mutation. /cart/update.js (attributes/note) is
  // intentionally NOT watched — our own sync uses it, and watching it would
  // risk a self-trigger loop.
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function(input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|clear)(\.js)?(\?|$)/.test(url)) {
        p.then(function() { setTimeout(syncAttrs, 200); }).catch(function() {});
      }
      return p;
    };
  }

  // Initial sync.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(syncAttrs, 50); });
  } else {
    setTimeout(syncAttrs, 50);
  }

  // Expose for manual debugging.
  window.__cartAttrsSync = syncAttrs;
  // Exposed for testing the invariant against a live cart without mutating it.
  window.__cartAddonAudit = function(items) {
    return {
      orphans: orphanPairedAddonKeys(items),
      excess: excessPairedAddonMutations(items)
    };
  };
})();
